import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import CookieBanner from "@/components/site/CookieBanner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSeo, ORG_SCHEMA, buildFaqSchema } from "@/lib/seo";

const Content = () => {
  const { t, lang } = useLang();
  const allItems = [...t.faq.items, ...t.solutions.broker.faq.items, ...t.solutions.supplier.faq.items];
  useSeo(lang, {
    title: lang === "nl"
      ? "Veelgestelde vragen over Tribeo | Makelaars en leveranciers"
      : "FAQ | Frequently Asked Questions | Tribeo",
    description: lang === "nl"
      ? "Antwoorden op de meest gestelde vragen van makelaars, leveranciers, kopers en verkopers over het Tribeo marketplace platform."
      : "Answers to the most common questions from agents, vendors, buyers and sellers about the Tribeo marketplace platform.",
    ogTitle: lang === "nl" ? "Veelgestelde vragen | Tribeo" : "Frequently asked questions | Tribeo",
    ogDescription: lang === "nl"
      ? "Alles over kosten, werking, leveranciers en betalingen. Antwoorden op de vragen die we het vaakst krijgen."
      : "Everything about costs, how it works, vendors and payouts.",
    keywords: lang === "nl" ? "tribeo veelgestelde vragen, tribeo faq, marketplace makelaar kosten" : "tribeo faq, tribeo agent questions, tribeo vendor questions",
    nlPath: "/faq",
    enPath: "/en/faq",
    jsonLd: [ORG_SCHEMA, buildFaqSchema(allItems)],
  });

  const groups = [
    { title: lang === "nl" ? "Over Tribeo" : "About Tribeo", items: t.faq.items },
    { title: lang === "nl" ? "Voor makelaars" : "For brokers", items: t.solutions.broker.faq.items },
    { title: lang === "nl" ? "Voor leveranciers" : "For suppliers", items: t.solutions.supplier.faq.items },
  ];

  return (
    <article className="container max-w-3xl py-32 md:py-40">
      <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">
        {lang === "nl" ? "Veelgestelde vragen" : "Frequently asked questions"}
      </h1>
      <p className="mt-4 text-lg text-muted-foreground">
        {lang === "nl"
          ? "Antwoorden op de vragen die we het vaakst krijgen van makelaars, leveranciers, kopers en verkopers."
          : "Answers to the questions we hear most often from brokers, suppliers, buyers and sellers."}
      </p>

      {groups.map((g) => (
        <section key={g.title} className="mt-12">
          <h2 className="text-2xl font-extrabold">{g.title}</h2>
          <Accordion type="single" collapsible className="mt-4">
            {g.items.map((it, i) => (
              <AccordionItem key={i} value={`${g.title}-${i}`} className="border-border">
                <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">{it.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{it.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      ))}
    </article>
  );
};

const FAQPage = () => (
  <LanguageProvider>
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <Content />
      </main>
      <Footer />
      <CookieBanner />
    </div>
  </LanguageProvider>
);

export default FAQPage;
